import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	getDocxFlowDocument,
	saveDocxFlowDocument,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service.ts';
import {
	useTranslationDefault,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import {
	getApiErrorMessage,
	isStandardBackendError,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';

export const buildDocxFlowDocumentQueryKey = (args: {
	dealId: string;
	programmeId: string;
	templateId: string;
}): string => `docx-flow-document-${args.dealId}-${args.programmeId}-${args.templateId}`;

export const useDocxFlowDocumentQuery = (templateId: string | null) => {
	const { dealId, mipId } = useRequiredParams('dealId', 'mipId');

	return useQuery({
		queryKey: [
			buildDocxFlowDocumentQueryKey({
				dealId,
				programmeId: mipId,
				templateId: templateId ?? '',
			}),
		],
		queryFn: () =>
			getDocxFlowDocument({ dealId, programmeId: mipId, templateId: templateId ?? '' }),
		enabled: !!templateId,
		// The editor holds the working copy in ProseMirror; refetching under it would discard
		// unsaved edits, so this is fetched once per open and only invalidated on save.
		staleTime: Infinity,
		refetchOnWindowFocus: false,
	});
};

export const useSaveDocxFlowDocument = () => {
	const tMipManage = useTranslationMipManage();
	const tDefault = useTranslationDefault();
	const queryClient = useQueryClient();
	const { dealId, mipId } = useRequiredParams('dealId', 'mipId');

	return useMutation({
		mutationFn: async (args: {
			templateId: string;
			document: unknown;
			bindings?: Record<string, string>;
		}) =>
			await saveDocxFlowDocument({
				dealId,
				programmeId: mipId,
				templateId: args.templateId,
				document: args.document,
				...(args.bindings === undefined ? {} : { bindings: args.bindings }),
			}),
		onSuccess: async (_result, { templateId }) => {
			await queryClient.invalidateQueries({
				queryKey: [
					buildDocxFlowDocumentQueryKey({ dealId, programmeId: mipId, templateId }),
				],
			});
			showToastV2({ title: tMipManage('master_documents.toast.updated') });
		},
		onError: (error: unknown) => {
			if (isStandardBackendError(error)) {
				showToastV2({
					state: 'error',
					title: tDefault(`error_codes.${error.error?.code}`),
				});
				return;
			}
			/**
			 * The server's own sentence, when it sent one.
			 *
			 * `saveDocument` refuses for five named reasons — a signature in a table header
			 * row, a shared instance, a template already issued to signers, an incomplete
			 * placement, a duplicated one — and each carries the wording that says what to do
			 * about it. None of them survives `isStandardBackendError`: the global filter only
			 * copies a `code` onto the wire for a `BusinessException`, and these are plain
			 * `BadRequestException`s, so every one of them arrived here as a bare "something
			 * went wrong" and the author was left with a Save button that did nothing and no
			 * way to find out why. Safe to show: these messages are authored for this toast
			 * and carry no ids.
			 */
			const reason = getApiErrorMessage(error);
			showToastV2({
				state: 'error',
				title: tDefault('common.toast.error.message'),
				...(reason === undefined ? {} : { message: reason }),
			});
		},
	});
};
